// a library to wrap and simplify api calls
import apisauce, { TIMEOUT_ERROR } from "apisauce";
import setupAuthInterceptors from "./authInterceptor";
import { notifyApiTimeout } from "./apiTimeoutEvents";

// ------
// STEP 1
// ------
//
// Create and configure an apisauce-based api object.
//
const api = apisauce.create({
  // base URL is read from the "constructor"
  baseURL: process.env.REACT_APP_API_URL,
  // here are some default headers
  headers: {
    "Cache-Control": "no-cache",
  },
  // Allow long-running operations up to two minutes.
  timeout: 120000,
});

api.addMonitor((response) => {
  if (response.problem === TIMEOUT_ERROR) {
    notifyApiTimeout();
  }
});

// ------
// STEP 2
// ------
//
// Define some functions that call the api.  The goal is to provide
// a thin wrapper of the api layer providing nicer feeling functions
// rather than "get", "post" and friends.
//
// I generally don't like wrapping the output at this level because
// sometimes specific actions need to be take on `403` or `401`, etc.
//
// Since we can't hide from that, we embrace it by getting out of the
// way at this level.
//
const setAuthToken = (userAuth) => api.setHeader("Authorization", "Bearer " + userAuth);
const removeAuthToken = () => api.deleteHeader("Authorization");

setupAuthInterceptors(api, setAuthToken);
const getAccount = () => api.get('api/v1/account')

const getOneSettlement = (settlementId) => api.get("api/v1/settlements/" + settlementId);
const getSettlement = (businessId, options) => api.get("api/v1/settlements?walletId.equals=" + businessId, options);
const getAllSettlements = (options) => api.get("api/v1/settlements/", options);
const getSettlementCount = (filter) => api.get(`api/v1/settlements/count?${filter}`);
const createSettlement = (settlement) => api.post("api/v1/settlements", settlement);
const updateSettlement = (settlement) => api.put("api/v1/settlements", settlement);
const deleteSettlement = (settlement) => api.delete("api/v1/settlements/" + settlement);
const getFilterSettlements = (filter, options) => api.get(`api/v1/settlements?${filter}&${options}`,);
const getProviderWallets = () => api.get("api/v1/settlements/providers/wallets");
const verifyAccount = (systemBankId, identifier) => api.get(`api/v1/system-banks/verify/${systemBankId}/${identifier}`)
const approveSettlement = (settlementId) => api.patch(`api/v1/settlements/process/${settlementId}`)
const getPaymentSources = (settlementId) => api.get(`api/v1/settlements/sources/settlement/${settlementId}`)
const reverseSettlement = (settlementId, data) => api.post(`api/v1/settlements/reverse/${settlementId}`, data)


const getPaymentSchedule = (paymentScheduleId) => api.get("api/v1/payment-schedules/" + paymentScheduleId);
const getPaymentSchedules = (options) => api.get("api/v1/payment-schedules", options);
const getPaymentScheduleCount = (params) => api.get("api/v1/payment-schedules/count", params);
const getPaymentScheduleRange = (businessId, startDate, endDate, options) => api.get("api/v1/payment-schedules/business/" + businessId + "/" + startDate + "/" + endDate, options);
const getPaymentScheduleSummary = (businessId, startDate, endDate, currentDate
) => api.get("api/v1/payment-schedules/summary/" + businessId + "/" + startDate + "/" + endDate + "/" + currentDate);
const getPaymentScheduleProjection = (businessId, startDate, endDate) => api.get("api/v1/payment-schedules/projection/business/" + businessId + "/" + startDate + "/" + endDate);
const createPaymentSchedule = (paymentSchedule) => api.post("api/v1/payment-schedules", paymentSchedule);
const updatePaymentSchedule = (paymentSchedule) => api.put("api/v1/payment-schedules", paymentSchedule);
const deletePaymentSchedule = (paymentScheduleId) => api.delete("api/v1/payment-schedules/" + paymentScheduleId);
const notifyPaymentSchedule = (paymentScheduleId) => api.post("api/v1/payment-schedules/notify/" + paymentScheduleId);
const getFilterPaymentSchedule = (filter, options) => api.get(`api/v1/payment-schedules?` + filter, options);
const getFilterPaymentCount = (filter) => api.get(`api/v1/payment-schedules/count?` + filter);
const externallyMarkPayment = (id, receivedDate) => api.patch(`api/v1/payment-schedules/${id}/mark-externally-paid?receivedOn=${receivedDate}`)
// MarkPaidInfo body — mode: ExternalMode (CASH | MOBILE_MONEY | BANK_TRANSFER | CHEQUE | UNKNOWN)
const markPlanPaymentExternallyPaid = (planId, markPaidInfo) =>
  api.post(`api/v1/payment-schedules/plan/${planId}/admin/mark-externally-paid`, markPaidInfo)
const getAllFilteredPaymentSchedule = (businessId, params) => api.get(`api/v1/_search/payment-schedules/${businessId}/all`, params);

const getBusiness = (businessId) => api.get("api/v1/businesses/" + businessId);
const getBusinesses = (options) => api.get("api/v1/businesses", options);
const getBusinessesCount = () => api.get("api/v1/businesses/count");
const createBusiness = (business) => api.post("api/v1/businesses", business);
const updateBusiness = (business) => api.put("api/v1/businesses", business);
const deleteBusiness = (businessId) => api.delete("api/v1/businesses/" + businessId);
const getCachedBusinesses = () => api.get("api/v1/businesses/cache");
const getBusinessesByCountry = (countryId) => api.get(`api/v1/countries/${countryId}/businesses`);
const getInvitationUptake = (businessId, planId, query, options) => api.get("api/v1/invitation-uptakes/" + businessId + "/" + planId + query, options);

const getEnterpriseCustomers = (businessId, options) => api.get("api/v1/business-customers?businessId.equals=" + businessId, options)
const getEnterpriseCustomersCount = (businessId) => api.get("api/v1/business-customers/count?businessId.equals=" + businessId)
const searchBusinessCustomersSummary = (businessId, params) => api.get(`api/v1/business-customers/_search/${businessId}/summary`, params);
const getExactFilteredBusinessCustomers = (businessId, query, options) =>
  api.get(`api/v1/business-customers/_search/${businessId}/exact/${encodeURIComponent(query)}`, options);
const getFilteredBusinessCustomers = (businessId, query) =>
  api.get(`api/v1/business-customers/_search/${businessId}/simple?searchText=${encodeURIComponent(query)}`);

const getPaymentTrackRange = (businessId, startDate, endDate, options) => api.get("api/v1/payment-tracks/business/" + businessId + "/" + startDate + "/" + endDate, options);
const getPaymentTrackCount = (options) => api.get("api/v1/payment-tracks/count", options);

const getPayoutChannel = (payoutChannelId) => api.get("api/v1/payout-channels/" + payoutChannelId);
const getPayoutChannels = (businessId, options) => api.get("api/v1/payout-channels?businessId.equals=" + businessId + "&status.equals=ACTIVE", options);
const createPayoutChannel = (payoutChannel) => api.post("api/v1/payout-channels", payoutChannel);
const updatePayoutChannel = (payoutChannel) => api.put("api/v1/payout-channels", payoutChannel);
const deletePayoutChannel = (payoutChannelId) => api.delete("api/v1/payout-channels/" + payoutChannelId);
const archivePayoutChannel = (payoutChannelId, username) => api.patch("api/v1/payout-channels/archive/" + payoutChannelId + "/" + username);

const getSubscription = (subscriptionId) => api.get("api/v1/subscribers/" + subscriptionId);
const getAllSubscriptions = (options) => api.get("api/v1/subscribers", options);
const getAllSearchSubscriptions = (businessId, params) => api.get(`api/v1/_search/subscribers/${businessId}`, params);
const getAllSearchSubscriptionsCount = (businessId, params) =>
  api.get(`api/v1/_search/subscribers/${businessId}/count`, params);

const getCountSubscriptions = (options) => api.get("api/v1/subscribers/count", options);
const searchSubscriptions = (filters, options) => api.get("api/v1/_search/subscribers/" + filters, options);
const dateFilterSubscriptions = (query, businessId, startDate, endDate, options) => api.get("api/v1/_search/subscribers/" + businessId + "/" + startDate + "/" + endDate + "?query=" + query, options);
const searchSubscriptionCount = (query, filters) => api.get("api/v1/_search/subscribers/count?query=" + query + "&filters=" + filters);
const pauseSubscription = (subscriptionId) => api.patch(`api/v1/subscribers/${subscriptionId}/pause`)
const cancelSubscription = (subscriptionId) => api.patch(`api/v1/subscribers/${subscriptionId}/cancel`)
const rescheduleSubscription = (subscriptionId, date) => api.patch(`api/v1/subscribers/reschedule/${subscriptionId}/` + date)

const getPlanGroupsGraphedActive = (businessId) => api.get('api/v1/plan-groups/graphed/active/' + businessId)
const getPlanGroupsGraphedAll = (businessId) => api.get(`api/v1/plan-groups/graphed/all/${businessId}`)
const getPlanGroupsGraphedArchived = (businessId) => api.get('api/v1/plan-groups/graphed/archived/' + businessId)
const getPlanGroups = (businessId, options) => api.get(`api/v1/plan-groups?businessId.equals=${businessId}`, options);
const getPlans = (businessId, options) => api.get("api/v1/plans?businessId.equals=" + businessId, options);
const createPlanGroup = (planGroup) => api.post("api/v1/plan-groups", planGroup);
const updatePlanGroup = (planGroupId, planGroup) => api.put(`api/v1/plan-groups/${planGroupId}`, planGroup);
const addProductWatcher = (watcher) => api.post("api/v1/watchers", watcher);
const removeProductWatcher = (watcherId) => api.delete(`api/v1/watchers/${watcherId}`);
const productWatchers = (planGroupId) => api.get(`api/v1/watchers/plan-group/${planGroupId}`);
const leaderBoard = (planGroupId) => api.post(`api/v1/plan-groups/generate-board/${planGroupId}`);
const createPlan = (plan) => api.post("api/v1/plans", plan);
const updatePlan = (planId, plan) => api.patch(`api/v1/plans/${planId}`, plan);
const getAutoBillingSupport = (businessId) => api.get(`api/v1/providers/auto-billing/${businessId}`);
const addWatchersToPlan = (watchers) => api.post(`api/v1/watchers`, watchers);
const removeWatcherFromPlan = (watcherId) => api.delete(`api/v1/watchers/${watcherId}`);
const getAllWatchers = (planId) => api.get(`api/v1/watchers/plan/${planId}`);
const getSegment = (businessId) => api.get(`api/v1/segments/business/${businessId}`);
const associateSegmentWithBusinessCustomer = (segmentId, bcId) =>
  api.post(`api/v1/segments/${segmentId}/associate/${bcId}`);
const getTagGroupsGraphed = (businessId) => api.get(`/api/v1/tag-groups/business/${businessId}/graphed`);
const associateTagWithBusinessCustomer = (tagId, bcId) =>
  api.post(`api/v1/tags/${tagId}/associate/${bcId}`);
const dissociateTagFromBusinessCustomer = (tagId, bcId) =>
  api.delete(`api/v1/tags/${tagId}/dissociate/${bcId}`);
const linkPlanWithBusinessCustomer = (businessCustomerId, planId) =>
  api.patch(
    `api/v1/business-customers/${businessCustomerId}/plans/${planId}/link`
  );
const getReportConfigurations = (businessId) =>
  api.get("api/v1/report-configurations", { businessId });
const createReportConfiguration = (reportConfiguration) =>
  api.post("api/v1/report-configurations", reportConfiguration);
const generateReportConfigurationXlsx = (configId) =>
  api.get(`api/v1/report-configurations/${configId}/generate.xlsx`, null, {
    responseType: "arraybuffer",
    headers: {
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  });
const deleteReportConfiguration = (configId) =>
  api.delete(`api/v1/report-configurations/${configId}`);

const getCredential = (businessId) => api.get("api/v1/credential/" + businessId);
const enableRequest = (businessId) => api.post("api/v1/credential/enable/" + businessId);
const generateRequest = (businessId) => api.patch("api/v1/credential/rotate/" + businessId);
const disableRequest = (businessId) => api.patch("api/v1/credential/disable/" + businessId);

const indexSubscribers = () => api.patch("api/v1/subscribers/index-all");
const indexPayments = () => api.patch("api/v1/payment-tracks/index-all");
const indexSignupRequests = () => api.patch("api/v1/self-signups/index-all");
const indexCustomers = () => api.patch("api/v1/customers/index-all");
const indexPaymentsSchedules = () => api.patch("api/v1/payment-schedules/index-all");
const indexPlans = () => api.patch("api/v1/plan-groups/index-all");
const indexBusinessCustomers = () => api.patch("api/v1/customers/business/index-all")
const indexActivities = () => api.patch("api/v1/activities/index-all")
const indexAllSettlements = () => api.patch("api/v1/settlements/index-all")

const getCountry = (id) => api.get("api/v1/countries/" + id)
const getCountries = (options) => api.get("api/v1/countries", options);
const updateCountry = (countryId, countryData) => api.put(`api/v1/countries/${countryId}`, countryData)
const getIdentityDocument = (id) => api.get("api/v1/identity-documents/" + id);
const getIdentityDocuments = (countryId, options) => api.get("api/v1/identity-documents?countryId.equals=" + countryId + options);
const createIdentityDocuments = (identityDocument) => api.post("api/v1/identity-documents", identityDocument);
const updateIdentityDocuments = (id, identityDocument) => api.put(`api/v1/identity-documents/${id}`, identityDocument);
const disableIdentityDocuments = (id) => api.patch(`api/v1/identity-documents/${id}/deactivate`);
const getIdentityDocumentsCount = (countryId) => api.get("api/v1/identity-documents/count?countryId.equals=" + countryId);
const getZones = () => api.get("api/v1/countries/zones");

const getCustomers = (options) => api.get('api/v1/customers', options);
const getCustomersCount = () => api.get('api/v1/customers/count');
const getBusinessCustomerRecord = (businessCustomerId) => api.get(`api/v1/business-customers/${businessCustomerId}`);
const getBusinessCustomerSummary = (businessCustomerId) =>
  api.get(`api/v1/business-customers/${businessCustomerId}/summary`);
const getBusinessCustomerSubscriptionsSummary = (businessCustomerId, options) =>
  api.get(`api/v1/business-customers/${businessCustomerId}/subscriptions/summary`, options);
const getBusinessCustomerSubscriptionAmounts = (bcId) =>
  api.get(`api/v1/business-customers/${bcId}/subscriptions/amounts`);
const getBusinessCustomerTags = (bcId) =>
  api.get(`api/v1/business-customers/${bcId}/tags`);
const linkBusinessCustomerToPrimary = (secondaryBusinessCustomerId, primaryBusinessCustomerId, reason) =>
  api.patch(
    `api/v1/business-customers/${secondaryBusinessCustomerId}/link/${primaryBusinessCustomerId}`,
    null,
    reason ? { params: { reason } } : undefined
  );
const unlinkBusinessCustomerFromPrimary = (secondaryBusinessCustomerId) =>
  api.patch(`api/v1/business-customers/${secondaryBusinessCustomerId}/unlink`);
const getCanonicalBusinessCustomer = (secondaryBusinessCustomerId) =>
  api.get(`api/v1/business-customers/${secondaryBusinessCustomerId}/canonical`);
const getSecondaryBusinessCustomers = (bcId, options) =>
  api.get(`api/v1/business-customers/${bcId}/secondaries`, options);
const getSecondaryBusinessCustomersCount = (bcId) =>
  api.get(`api/v1/business-customers/${bcId}/secondaries/count`);
const getBusinessCustomerLinkedPlans = (bcId) =>
  api.get(`api/v1/business-customers/${bcId}/plans`);
const getBusinessCustomerLinkedPlansCount = (bcId) =>
  api.get(`api/v1/business-customers/${bcId}/plans/count`);
const createBusinessCustomer = (businessId, customerDto) =>
  api.post(`api/v1/business-customers/${businessId}/create`, customerDto);
const updateBusinessCustomerSimple = (id, customerDto) =>
  api.put(`api/v1/business-customers/update/simple/${id}`, customerDto);

const sendBulkInvite = (businessId, inviteData) => api.post(`api/v1/customer-imports/invite/${businessId}`, inviteData);

const getPendingVerifications = (options) => api.get("api/v1/identity-verif-requests?status.equals=PENDING", options);
const getPendingVerificationsCount = () => api.get("api/v1/identity-verif-requests/count?status.equals=PENDING");
const getApprovedVerifications = (options) => api.get("api/v1/identity-verif-requests?status.equals=APPROVED", options);
const getApprovedVerificationsCount = () => api.get("api/v1/identity-verif-requests/count?status.equals=APPROVED");
const getRejectedVerifications = (options) => api.get("api/v1/identity-verif-requests?status.equals=REJECTED", options);
const getRejectedVerificationsCount = () => api.get("api/v1/identity-verif-requests/count?status.equals=REJECTED");
const pendingVerifications = () => api.get('api/v1/identity-verif-requests?status.equals=PENDING')
const pendingVerification = (id) => api.get('api/v1/identity-verif-requests/' + id)

const getVerificationProofRequest = (requestId) => api.get(`api/v1/identity-proofs/request/${requestId}`)
const getIdentityImage = (proofId) => api.get('api/v1/identity-proofs/' + proofId + '/image')
const approveVerification = (id, username) => api.patch(`api/v1/identity-verif-requests/${id}/${username}/approve`)
const rejectVerification = (id, username, message) => api.patch('api/v1/identity-verif-requests/' + id + '/' + username +
  '/reject?rejectionMsg=' + message)
const approvedVerificationDetail = (businessId) => api.get(`api/v1/identity-verif-requests/approval/${businessId}`);

const buildSystemBankCriteriaQuery = (filters = {}) => {
  const params = [];
  if (filters.countryId) {
    params.push(`countryId.equals=${filters.countryId}`);
  }
  if (filters.name) {
    params.push(`name.contains=${encodeURIComponent(filters.name)}`);
  }
  if (filters.type) {
    params.push(`type.equals=${filters.type}`);
  }
  if (filters.active !== undefined && filters.active !== null && filters.active !== "") {
    params.push(`active.equals=${filters.active}`);
  }
  return params.join("&");
};

const getSystemBanks = (filters, options) => {
  const criteria = buildSystemBankCriteriaQuery(filters);
  const url = criteria ? `api/v1/system-banks?${criteria}` : "api/v1/system-banks";
  return api.get(url, options);
};
const getSystemBank = (Id) => api.get(`api/v1/system-banks/${Id}`);
const getSystemBanksCount = (filters = {}) => {
  const criteria = buildSystemBankCriteriaQuery(filters);
  const url = criteria ? `api/v1/system-banks/count?${criteria}` : "api/v1/system-banks/count";
  return api.get(url);
};
const logoUpload = (bankId, file) => api.post(`api/v1/system-banks/upload/${bankId}/logo`, file, {
  headers: {
    'content-type': 'multipart/form-data'
  }
})
const disableSystemBank = (id) => api.patch(`api/v1/system-banks/${id}/deactivate`)
const getProviderBanks = (systemBankId) => api.get("api/v1/banks?systemBankId.equals=" + systemBankId)
const buildBankCriteriaQuery = (filters = {}) => {
  const params = [];
  if (filters.paymentProvider) {
    params.push(`paymentProvider.equals=${filters.paymentProvider}`);
  }
  if (filters.country) {
    params.push(`country.contains=${encodeURIComponent(filters.country)}`);
  }
  if (filters.bankName) {
    params.push(`bankName.contains=${encodeURIComponent(filters.bankName)}`);
  }
  if (filters.bankCode) {
    params.push(`bankCode.contains=${encodeURIComponent(filters.bankCode)}`);
  }
  if (filters.bankType) {
    params.push(`bankType.equals=${filters.bankType}`);
  }
  if (filters.systemBankId) {
    params.push(`systemBankId.equals=${filters.systemBankId}`);
  }
  if (filters.systemBankLinked === true) {
    params.push("systemBankId.specified=true");
  }
  if (filters.systemBankLinked === false) {
    params.push("systemBankId.specified=false");
  }
  return params.join("&");
};
const getBanks = (filters, options) => {
  const criteria = buildBankCriteriaQuery(filters);
  const url = criteria ? `api/v1/banks?${criteria}` : "api/v1/banks";
  return api.get(url, options);
};
const getBanksCount = (filters = {}) => {
  const criteria = buildBankCriteriaQuery(filters);
  const url = criteria ? `api/v1/banks/count?${criteria}` : "api/v1/banks/count";
  return api.get(url);
};
const getProviders = () => api.get(`api/v1/settlements/providers`)
const getBankPerProvider = (paymentProvider) => api.get(`api/v1/banks/provider/${paymentProvider}`)
const linkProviderToBank = (systemBankId, providerBankId) => api.patch(`api/v1/system-banks/${systemBankId}/link/${providerBankId}`)
const unlinkProviderBank = (systemBankId, providerBankId) => api.patch(`api/v1/system-banks/${systemBankId}/unlink/${providerBankId}`)
const disablePaystackOtp = () => api.patch("api/v1/system-banks/paystack/disable-otp")
const confirmOtpDisable = (otp) => api.patch(`api/v1/system-banks/paystack/confirm-disable-otp/${otp}`)
const updatePaystackReveknewSubaccountBanks = (payload = {}) =>
  api.post("api/v1/providers/paystack/reveknew-subaccounts/bank-update", payload)

const getCustomerImports = (businessId, options) => api.get("api/v1/customer-imports?businessId.equals=" + businessId, options);
const getCustomerImport = (importId) => api.get(`api/v1/customer-imports/${importId}`);
const uploadCustomerImports = (businessId, audienceId, name, file) => api.post(`api/v1/customer-imports/upload/${businessId}?audienceId=${encodeURIComponent(audienceId)}&name=${encodeURIComponent(name)}`, file, {
  headers: {
    "content-type": "multipart/form-data",
  },
});

const businessCustomerCsvDownload = () => api.get('api/v1/customer-imports/template/download', {}, {
  headers: { 'Accept': "text/csv" },
});

const buildApplyQuery = (batchNo, mode) => {
  const params = new URLSearchParams();
  if (batchNo != null && String(batchNo).trim() !== '') {
    params.set('batchNo', batchNo);
  }
  if (mode) {
    params.set('mode', mode);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

const applyImport = (id, batchNo, mode) =>
  api.put(`api/v1/customer-imports/apply/${id}${buildApplyQuery(batchNo, mode)}`)
const getEntriesList = (importId) => api.get(`api/v1/customer-import-entries/imports/${importId}`)
const createAudience = (audienceDto) => api.post("api/v1/audiences", audienceDto);
const getAudienceMembers = (audienceId, options) => api.get(`api/v1/audiences/${audienceId}/members`, options)
const addAudienceMember = (audienceId, bcId) =>
  api.post(`api/v1/audiences/${audienceId}/members/${bcId}`)
const removeAudienceMember = (audienceId, memberId) =>
  api.delete(`api/v1/audiences/${audienceId}/members/${memberId}`)
const getAudienceCandidateSegments = (audienceId) => api.get(`api/v1/audiences/${audienceId}/candidate-segments`)
const getAudienceCandidateTags = (audienceId) => api.get(`api/v1/audiences/${audienceId}/candidate-tags`)
const deleteAudienceCandidateSegment = (audienceId, candidateSegmentId) =>
  api.delete(`api/v1/audiences/${audienceId}/candidate-segments/${candidateSegmentId}`)
const deleteAudienceCandidateTag = (audienceId, candidateTagId) =>
  api.delete(`api/v1/audiences/${audienceId}/candidate-tags/${candidateTagId}`)
const saveAudienceCandidateMetadata = (audienceId, metadata) =>
  api.post(`api/v1/audiences/${audienceId}/candidate-metadata`, metadata)
const applyAudienceCandidates = (audienceId) =>
  api.post(`api/v1/audiences/${audienceId}/apply-candidates`)
const applyAudienceSegment = (audienceId, segmentId) =>
  api.post(`api/v1/audiences/${audienceId}/apply-segment/${segmentId}`)
const applyAudienceBatch = (audienceId, batchNo, mode) =>
  api.put(`api/v1/audiences/apply/${audienceId}${buildApplyQuery(batchNo, mode)}`)
const downloadAudienceMembers = (audienceId) => api.get(`api/v1/audiences/${audienceId}/download`, {}, {
  headers: { 'Accept': "text/csv" },
});
const submitCustomerEnrichment = (businessId, file) => api.post(`api/v1/business-customers/${businessId}/enrich`, file, {
  headers: {
    "content-type": "multipart/form-data",
  },
});
const getCustomerEnrichments = (businessId, options) =>
  api.get(`api/v1/business-customers/${businessId}/enrich`, options);
const getCustomerEnrichmentStatus = (businessId, requestId) =>
  api.get(`api/v1/business-customers/${businessId}/enrich/${requestId}`);
const getCustomerEnrichmentItems = (businessId, requestId, options) =>
  api.get(`api/v1/business-customers/${businessId}/enrich/${requestId}/items`, options);
const getAudienceBulkInvitations = (audienceId) =>
  api.get(`api/v1/audiences/bulk-invitations/${audienceId}`)
const getAudienceBatches = (audienceId) =>
  api.get(`api/v1/audiences/${audienceId}/batches`)
const resubmitBulkInvitation = (invitationId) =>
  api.post(`api/v1/audiences/bulk-invitations/resubmit/${invitationId}`)

const getFilteredDlrs = (businessId, options) => api.get(`api/v1/sms-dlrs?businessId.equals= ${businessId}`, options)
const getAllDLRs = (options) => api.get("api/v1/sms-dlrs", options)
const getAllDLRsCount = () => api.get("api/v1/sms-dlrs/count")
const getFilteredCountDLRs = (businessId) => api.get("api/v1/sms-dlrs/count?businessId.equals=" + businessId)

const getEmailTemplate = () => api.get(`api/v1/email-templates`)
const getFilteredNameEmailTemplate = (filter) => api.get(`api/v1/email-templates?${filter}`)
const createEmailTemplate = (file) => api.post(`api/v1/email-templates`, file)
const updateEmailTemplate = (id, file) => api.patch(`api/v1/email-templates/${id}`, file)
const deleteEmailTemplate = (id) => api.delete(`api/v1/email-templates/${id}`);

const getIndustries = () => api.get(`api/v1/industries?size=2000&sort=nameEn,asc`)
const createIndustry = (industry) => api.post(`api/v1/industries`, industry)
const updateIndustry = (id, industry) => api.put(`api/v1/industries/${id}`, industry)

const getWhatsAppEmbeddedSignupConfig = () => api.get("api/v1/whatsapp/embedded-signup/config");
const completeWhatsAppEmbeddedSignup = (payload) => api.post("api/v1/whatsapp/embedded-signup/complete", payload);
const getWhatsAppPlatformDefaultAccount = () => api.get("api/v1/whatsapp/accounts/default");

const PENDING_MERCHANT_AGREEMENT_STATUSES_QUERY = "status.in=PENDING&status.in=SENT";
const SIGNED_MERCHANT_AGREEMENT_STATUSES_QUERY = "status.in=CLIENT_SIGNED&status.in=FULLY_SIGNED";

const getBusinessMerchantAgreements = (businessId, options) =>
  api.get(`api/v1/businesses/${businessId}/merchant-agreements`, options);
const getBusinessMerchantAgreementsCount = (businessId, options) =>
  api.get(`api/v1/businesses/${businessId}/merchant-agreements/count`, options);
const getBusinessPendingMerchantAgreementsCount = (businessId) =>
  api.get(
    `api/v1/businesses/${businessId}/merchant-agreements/count?${PENDING_MERCHANT_AGREEMENT_STATUSES_QUERY}`
  );
const getBusinessSignedMerchantAgreementsCount = (businessId) =>
  api.get(
    `api/v1/businesses/${businessId}/merchant-agreements/count?${SIGNED_MERCHANT_AGREEMENT_STATUSES_QUERY}`
  );
const generateMerchantAgreement = (businessId, provider) =>
  api.post(`api/v1/businesses/${businessId}/merchant-agreements/${provider}/generate`);
const downloadMerchantAgreement = (id) =>
  api.get(`api/v1/merchant-agreements/${id}/download`, {}, { responseType: "blob" });
const deleteMerchantAgreement = (id) =>
  api.delete(`api/v1/merchant-agreements/${id}`);
const updateBusinessSigner = (businessId, signer) =>
  api.patch(`api/v1/businesses/${businessId}/merchant-agreements/signer`, {
    signerEmail: signer.signerEmail,
    signerFullName: signer.signerFullName,
    signerCapacity: signer.signerCapacity,
  });
const getPaymentCredentialsConfigured = (businessId) =>
  api.get(`api/v1/businesses/payment-credentials/${businessId}/configured`);

const enableCardPayments = (businessId) => api.patch(`api/v1/businesses/card-payments/${businessId}/toggle`);
const passingFeesOn = (businessId) => api.patch(`api/v1/businesses/fees-passed-on/${businessId}/toggle`);
const overrideMomoFees = (businessId, fees) => api.patch(`api/v1/businesses/momo-fees/${businessId}/override/${fees}`);
const overrideCardFees = (businessId, fees) => api.patch(`api/v1/businesses/card-fees/${businessId}/override/${fees}`);
const enableCustomPSP = (businessId) => api.patch(`api/v1/businesses/custom-pmt-provider/${businessId}/toggle`);
const toggleReqsEnabled = (businessId) => api.patch(`api/v1/businesses/reqs-enabled/${businessId}/toggle`);
const getSignupReqs = (businessId) => api.get(`api/v1/businesses/${businessId}/signup-reqs`);
const createSignupReqs = (businessId, tagGroupIds) =>
  api.post(`api/v1/businesses/${businessId}/signup-reqs`, tagGroupIds);
const deleteSignupReq = (businessId, signupReqId) =>
  api.delete(`api/v1/businesses/${businessId}/signup-reqs/${signupReqId}`);
const getSignupReqExclusions = (businessId) =>
  api.get(`api/v1/businesses/${businessId}/signup-req-exclusions`);
const createSignupReqExclusions = (businessId, planGroupIds) =>
  api.post(`api/v1/businesses/${businessId}/signup-req-exclusions`, planGroupIds);
const deleteSignupReqExclusion = (businessId, exclusionId) =>
  api.delete(`api/v1/businesses/${businessId}/signup-req-exclusions/${exclusionId}`);

const businessVerificationRequest = (businessId) => api.get(`api/v1/identity-verif-requests/count?businessId.equals=${businessId}&status.equals=PENDING`);
const verificationStartedRequest = (businessId) => api.patch(`api/v1/identity-verif-requests/${businessId}/auto-create`);

const getBusinessesByName = (nameQuery, options) => api.get(`api/v1/businesses?name.contains=${nameQuery}`, options);
const getBusinessesByNameCount = (nameQuery) => api.get(`api/v1/businesses/count?name.contains=${nameQuery}`);

const getCollectedPaymentTrack = (businessId, filter, options) => api.get(`api/v1/_search/payment-tracks/${businessId}${filter}`, options);
const getMissedPaymentTrack = (businessId, filter, options) => api.get(`api/v1/_search/payment-schedules/${businessId}/missed${filter}`, options);
const getExternalPaymentTrack = (businessId, filter, options) => api.get(`api/v1/_search/payment-schedules/${businessId}/external${filter}`, options);
const getPaymentAttributions = (paymentTrackId) => api.get(`api/v1/payment-tracks/${paymentTrackId}/attributions`)
const getAccountsPaymentSearch = (businessId, filter, options) => api.get(`api/v1/_search/payment-schedules/${businessId}/accounts${filter}`, options);
const getAccountsPaymentCSV = (businessId, query, options) => api.get(`api/v1/payment-schedules/${businessId}/accounts/csv?${query}`, options, {
  headers: { 'Accept': '*/*' },
  'Content-Type': "text/csv"
});
const getAccountsPaymentPDF = (businessId, membershipId, query, options) => api.get(`api/v1/payment-schedules/${businessId}/${membershipId}/accounts/pdf?${query}`, options, {
  responseType: 'blob',
});
const getAccountsPaymentTotals = (businessId, filter, options) => api.get(`api/v1/payment-schedules/${businessId}/accounts/totals${filter}`, options);
const getExternalPaymentTotals = (businessId, filter, options) => api.get(`api/v1/payment-schedules/${businessId}/external/totals${filter}`, options);
const getExternalPaymentCSV = (businessId, query, options) => api.get(`api/v1/payment-schedules/${businessId}/external/csv?${query}`, options, {
  headers: {
    'Accept': '*/*',
  },
  'Content-Type': "text/csv"
});
const getExternalPaymentPDF = (businessId, membershipId, query, options) => api.get(`api/v1/payment-schedules/${businessId}/${membershipId}/external/pdf?${query}`, options, {
  responseType: 'blob',
});
const getFilteredDataTotals = (businessId, filter, options) => api.get(`api/v1/payment-tracks/${businessId}/totals${filter}`, options);
const getMemberships = (businessId, options) => api.get(`api/v1/memberships?businessId.equals=${businessId}`, options);
const getColleagues = (businessId, options) => api.get(`api/v1/memberships/${businessId}/colleagues`, options);


const getCollectedPaymentCSV = (businessId, membershipId, query, options) => api.get(`api/v1/payment-tracks/${businessId}/${membershipId}/csv?${query}`, options,
  {
    headers: {
      'Accept': '*/*',
    },
    'Content-Type': "text/csv"
  }
);
const getCollectedPaymentPDF = (businessId, membershipId, query, options) => api.get(`api/v1/payment-tracks/${businessId}/${membershipId}/pdf?${query}`, options,
  { responseType: 'blob', });

const getPlanPaymentSummary = (businessId, startDate, endDate) =>
  api.get(`api/v1/payment-tracks/${businessId}/summary/${startDate}/${endDate}`);
const getPlanReconciliationSummary = (businessId, startDate, endDate) =>
  api.get(`api/v1/payment-tracks/${businessId}/reconciliation/${startDate}/${endDate}`);
const getExternalColleagueSummary = (businessId, startDate, endDate, membershipId) => {
  const query = membershipId != null && membershipId !== ''
    ? `?membershipId=${encodeURIComponent(membershipId)}`
    : '';
  return api.get(`api/v1/payment-schedules/${businessId}/external/summary/${startDate}/${endDate}${query}`);
};
const getExternalColleagueSummaryCsv = (businessId, startDate, endDate, membershipId) => {
  const query = membershipId != null && membershipId !== ''
    ? `?membershipId=${encodeURIComponent(membershipId)}`
    : '';
  return api.get(
    `api/v1/payment-schedules/${businessId}/external/summary/${startDate}/${endDate}/csv${query}`,
    {},
    {
      headers: { Accept: '*/*' },
      'Content-Type': 'text/csv',
    }
  );
};
const getExternalColleagueSummaryPdf = (businessId, membershipId, startDate, endDate, recordedById) => {
  const query = recordedById != null && recordedById !== ''
    ? `?recordedById=${encodeURIComponent(recordedById)}`
    : '';
  return api.get(
    `api/v1/payment-schedules/${businessId}/${membershipId}/external/summary/${startDate}/${endDate}/pdf${query}`,
    {},
    { responseType: 'blob' }
  );
};
const getPaymentsSummaryPdf = (businessId, membershipId, startDate, endDate) =>
  api.get(
    `/api/v1/payment-tracks/${businessId}/${membershipId}/summary/${startDate}/${endDate}/pdf`,
    {},
    { responseType: 'blob' }
  );
const getReconciliationSummaryPdf = (businessId, membershipId, startDate, endDate) =>
  api.get(
    `/api/v1/payment-tracks/${businessId}/${membershipId}/reconciliation/${startDate}/${endDate}/pdf`,
    {},
    { responseType: 'blob' }
  );

const createSegment = (segment) => api.post("api/v1/segments", segment);
const createTag = (tag) => api.post("api/v1/tags", tag);
const updateTag = (tagId, tag) => api.put(`api/v1/tags/${tagId}`, tag);

const createTagGroup = (tagGroup) => api.post("api/v1/tag-groups", tagGroup);
const updateTagGroup = (groupId, tagGroup) => api.put(`api/v1/tag-groups/${groupId}`, tagGroup);

const getTags = (businessId) => api.get(`api/v1/tags/business/${businessId}`);
const getTagGroups = (businessId) => api.get(`api/v1/tag-groups/business/${businessId}`);

const buildPlatformReportParams = ({ startDate, endDate, businessId } = {}) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (businessId) params.businessId = businessId;
  return params;
};

const getPlatformDailyReport = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/daily`, buildPlatformReportParams(params));

const getPlatformMonthlyReport = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/monthly`, buildPlatformReportParams(params));

const getPlatformYearlyReport = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/yearly`, buildPlatformReportParams(params));

const getPlatformDailyReportCsv = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/daily/csv`, buildPlatformReportParams(params), {
    responseType: "text",
    headers: { Accept: "text/csv" },
  });

const getPlatformMonthlyReportCsv = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/monthly/csv`, buildPlatformReportParams(params), {
    responseType: "text",
    headers: { Accept: "text/csv" },
  });

const getPlatformYearlyReportCsv = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/yearly/csv`, buildPlatformReportParams(params), {
    responseType: "text",
    headers: { Accept: "text/csv" },
  });

const getPlatformDailyReportPdf = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/daily/pdf`, buildPlatformReportParams(params), {
    responseType: "blob",
  });

const getPlatformMonthlyReportPdf = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/monthly/pdf`, buildPlatformReportParams(params), {
    responseType: "blob",
  });

const getPlatformYearlyReportPdf = (countryId, params) =>
  api.get(`api/v1/reports/platform/countries/${countryId}/yearly/pdf`, buildPlatformReportParams(params), {
    responseType: "blob",
  });

const buildInvoiceCriteriaQuery = (filters = {}) => {
  const params = [];
  if (filters.countryId) {
    params.push(`countryId.equals=${filters.countryId}`);
  }
  if (filters.businessId) {
    params.push(`businessId.equals=${filters.businessId}`);
  }
  if (filters.invYear) {
    params.push(`invYear.equals=${filters.invYear}`);
  }
  if (filters.invMonth) {
    params.push(`invMonth.equals=${filters.invMonth}`);
  }
  return params.join("&");
};

const getInvoices = (filters = {}, options = {}) => {
  const criteria = buildInvoiceCriteriaQuery(filters);
  const { page, size, sort } = options;
  const parts = [];
  if (criteria) {
    parts.push(criteria);
  }
  if (page != null) {
    parts.push(`page=${page}`);
  }
  if (size != null) {
    parts.push(`size=${size}`);
  }
  const sortValues = Array.isArray(sort) ? sort : sort ? [sort] : [];
  sortValues.forEach((value) => {
    parts.push(`sort=${value}`);
  });
  const query = parts.join("&");
  return api.get(query ? `api/v1/invoices?${query}` : "api/v1/invoices");
};

const getInvoicesCount = (filters = {}) => {
  const criteria = buildInvoiceCriteriaQuery(filters);
  const url = criteria ? `api/v1/invoices/count?${criteria}` : "api/v1/invoices/count";
  return api.get(url);
};

const getInvoice = (id) => api.get(`api/v1/invoices/${id}`);

const generateInvoice = (payload) => api.patch("api/v1/invoices/generate", payload);

const submitInvoice = (id) => api.patch(`api/v1/invoices/submit/${id}`);

const downloadInvoicePdf = (id) =>
  api.get(`api/v1/invoices/${id}/download`, {}, { responseType: "blob" });

// ignite-jhipster-api-method-needle

// ------
// STEP 3
// ------
//
// Return back a collection of functions that we would consider our
// interface.  Most of the time it'll be just the list of all the
// methods in step 2.
//
// Notice we're not returning back the `api` created in step 1?  That's
// because it is scoped privately.  This is one way to create truly
// private scoped goodies in JavaScript.
//
export {
  // a list of the API functions from step 2

  setAuthToken,
  removeAuthToken,
  getAccount,
  getOneSettlement,
  getSettlement,
  getAllSettlements,
  createSettlement,
  updateSettlement,
  deleteSettlement,
  getSettlementCount,
  getFilterSettlements,
  getProviderWallets,
  verifyAccount,
  approveSettlement,
  getPaymentSources,
  reverseSettlement,

  getPaymentSchedule,
  getPaymentSchedules,
  getPaymentScheduleCount,
  getPaymentScheduleRange,
  getPaymentScheduleSummary,
  getPaymentScheduleProjection,
  createPaymentSchedule,
  updatePaymentSchedule,
  deletePaymentSchedule,
  notifyPaymentSchedule,
  getFilterPaymentSchedule,
  getFilterPaymentCount,
  externallyMarkPayment,
  markPlanPaymentExternallyPaid,
  getAllFilteredPaymentSchedule,

  getBusiness,
  getBusinesses,
  getBusinessesCount,
  createBusiness,
  updateBusiness,
  deleteBusiness,
  getCachedBusinesses,
  getBusinessesByCountry,
  getInvitationUptake,
  getEnterpriseCustomers,
  getEnterpriseCustomersCount,
  searchBusinessCustomersSummary,
  getExactFilteredBusinessCustomers,
  getFilteredBusinessCustomers,
  businessVerificationRequest,
  verificationStartedRequest,


  getPaymentTrackRange,
  getPaymentTrackCount,
  getPayoutChannel,
  getPayoutChannels,
  createPayoutChannel,
  updatePayoutChannel,
  deletePayoutChannel,
  archivePayoutChannel,

  getSubscription,
  getAllSubscriptions,
  getCountSubscriptions,
  getAllSearchSubscriptions,
  getAllSearchSubscriptionsCount,
  searchSubscriptions,
  dateFilterSubscriptions,
  searchSubscriptionCount,
  pauseSubscription,
  cancelSubscription,
  rescheduleSubscription,

  getPlanGroupsGraphedArchived,
  getPlanGroupsGraphedActive,
  getPlanGroupsGraphedAll,
  getPlanGroups,
  getPlans,
  createPlanGroup,
  updatePlanGroup,
  addProductWatcher,
  removeProductWatcher,
  productWatchers,
  leaderBoard,
  createPlan,
  updatePlan,
  getAutoBillingSupport,
  addWatchersToPlan,
  removeWatcherFromPlan,
  getAllWatchers,
  getSegment,
  associateSegmentWithBusinessCustomer,
  getTagGroupsGraphed,
  associateTagWithBusinessCustomer,
  dissociateTagFromBusinessCustomer,
  linkPlanWithBusinessCustomer,
  getReportConfigurations,
  createReportConfiguration,
  generateReportConfigurationXlsx,
  deleteReportConfiguration,

  getCredential,
  enableRequest,
  generateRequest,
  disableRequest,

  indexSubscribers,
  indexPayments,
  indexSignupRequests,
  indexCustomers,
  indexPaymentsSchedules,
  indexPlans,
  indexBusinessCustomers,
  indexActivities,
  indexAllSettlements,

  getCountry,
  getCountries,
  updateCountry,
  getIdentityDocuments,
  createIdentityDocuments,
  updateIdentityDocuments,
  disableIdentityDocuments,
  getIdentityDocument,
  getIdentityDocumentsCount,
  getZones,

  getCustomers,
  getCustomersCount,
  getBusinessCustomerRecord,
  getBusinessCustomerSummary,
  getBusinessCustomerSubscriptionsSummary,
  getBusinessCustomerSubscriptionAmounts,
  getBusinessCustomerTags,
  linkBusinessCustomerToPrimary,
  unlinkBusinessCustomerFromPrimary,
  getCanonicalBusinessCustomer,
  getSecondaryBusinessCustomers,
  getSecondaryBusinessCustomersCount,
  getBusinessCustomerLinkedPlans,
  getBusinessCustomerLinkedPlansCount,
  createBusinessCustomer,
  updateBusinessCustomerSimple,
  sendBulkInvite,
  // checkExclusivity,

  getPendingVerifications,
  getApprovedVerifications,
  getRejectedVerifications,
  getPendingVerificationsCount,
  getApprovedVerificationsCount,
  getRejectedVerificationsCount,

  getSystemBanks,
  getSystemBank,
  getSystemBanksCount,
  getBanks,
  getBanksCount,
  logoUpload,
  disableSystemBank,
  getProviderBanks,
  getProviders,
  getBankPerProvider,
  linkProviderToBank,
  unlinkProviderBank,
  disablePaystackOtp,
  confirmOtpDisable,
  updatePaystackReveknewSubaccountBanks,

  pendingVerifications,
  pendingVerification,
  approveVerification,
  rejectVerification,
  getVerificationProofRequest,
  approvedVerificationDetail,
  getIdentityImage,

  getCustomerImports,
  getCustomerImport,
  uploadCustomerImports,
  businessCustomerCsvDownload,
  applyImport,
  getEntriesList,
  createAudience,
  getAudienceMembers,
  addAudienceMember,
  removeAudienceMember,
  getAudienceCandidateSegments,
  getAudienceCandidateTags,
  deleteAudienceCandidateSegment,
  deleteAudienceCandidateTag,
  saveAudienceCandidateMetadata,
  applyAudienceCandidates,
  applyAudienceSegment,
  applyAudienceBatch,
  downloadAudienceMembers,
  submitCustomerEnrichment,
  getCustomerEnrichments,
  getCustomerEnrichmentStatus,
  getCustomerEnrichmentItems,
  getAudienceBulkInvitations,
  getAudienceBatches,
  resubmitBulkInvitation,

  getFilteredDlrs,
  getAllDLRs,
  getAllDLRsCount,
  getFilteredCountDLRs,

  getEmailTemplate,
  getFilteredNameEmailTemplate,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,

  getIndustries,
  createIndustry,
  updateIndustry,

  getWhatsAppEmbeddedSignupConfig,
  completeWhatsAppEmbeddedSignup,
  getWhatsAppPlatformDefaultAccount,

  getBusinessMerchantAgreements,
  getBusinessMerchantAgreementsCount,
  getBusinessPendingMerchantAgreementsCount,
  getBusinessSignedMerchantAgreementsCount,
  generateMerchantAgreement,
  downloadMerchantAgreement,
  deleteMerchantAgreement,
  updateBusinessSigner,
  getPaymentCredentialsConfigured,

  enableCardPayments,
  passingFeesOn,
  overrideMomoFees,
  overrideCardFees,
  enableCustomPSP,
  toggleReqsEnabled,
  getSignupReqs,
  createSignupReqs,
  deleteSignupReq,
  getSignupReqExclusions,
  createSignupReqExclusions,
  deleteSignupReqExclusion,

  getBusinessesByName,
  getBusinessesByNameCount,
  getCollectedPaymentTrack,
  getMissedPaymentTrack,
  getExternalPaymentTrack,
  getPaymentAttributions,
  getAccountsPaymentSearch,
  getAccountsPaymentPDF,
  getAccountsPaymentCSV,
  getAccountsPaymentTotals,
  getExternalPaymentTotals,
  getExternalPaymentCSV,
  getExternalPaymentPDF,
  getFilteredDataTotals,
  getCollectedPaymentCSV,
  getCollectedPaymentPDF,
  getPlanPaymentSummary,
  getPlanReconciliationSummary,
  getExternalColleagueSummary,
  getExternalColleagueSummaryCsv,
  getExternalColleagueSummaryPdf,
  getPaymentsSummaryPdf,
  getReconciliationSummaryPdf,
  getMemberships,
  getColleagues,

  getTags,
  createSegment,
  createTag,
  updateTag,
  createTagGroup,
  updateTagGroup,
  getTagGroups,

  getPlatformDailyReport,
  getPlatformMonthlyReport,
  getPlatformYearlyReport,
  getPlatformDailyReportCsv,
  getPlatformMonthlyReportCsv,
  getPlatformYearlyReportCsv,
  getPlatformDailyReportPdf,
  getPlatformMonthlyReportPdf,
  getPlatformYearlyReportPdf,

  getInvoices,
  getInvoicesCount,
  getInvoice,
  generateInvoice,
  submitInvoice,
  downloadInvoicePdf,
};
